import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { BusinessPremise, Organization } from '../../../shared/sdk';
import { FormBuilder, Validators, FormGroup } from '@angular/forms';
import { BusinessPremiseValidator } from '../business-premise.validator';
import { FursService } from '../furs.service';
import { OrganizationService } from '../../../organizations/organization.service';
import { ToastService } from '../../../shared/modules/toast/toast.service';
import * as moment from 'moment';
import { Subject } from 'rxjs';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { takeUntil, filter, take } from 'rxjs/operators';
import Decimal from 'decimal.js';
import { TranslateService } from '@ngx-translate/core';
import { AccountService } from '../../../accounts/account.service';

@Component({
  selector: 'app-business-premise-add',
  templateUrl: './business-premise-add.component.html',
  styleUrls: ['./business-premise-add.component.css'],
})
export class BusinessPremiseAddComponent implements OnInit, OnDestroy {
  @Input() premise: BusinessPremise;
  @Input() strategy: 'C' | 'B';
  @Input() existingPremises: BusinessPremise[];
  businessPremiseForm: FormGroup;
  sent = false;
  loading = false;
  editing: boolean;
  activeOrg: Organization;
  private onDestroy$: Subject<void> = new Subject();

  constructor(
    private activeModal: NgbActiveModal,
    private fursService: FursService,
    private orgService: OrganizationService,
    private fb: FormBuilder,
    private toastService: ToastService,
    private translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.businessPremiseForm = this.fb.group(
      {
        type: ['realestate', Validators.required], // realestate/movable
        businessPremiseId: [
          '',
          [Validators.required, Validators.pattern('^[a-zA-Z0-9]*$')],
        ],
        street: [''],
        houseNumber: [''],
        houseNumberAdditional: [null],
        community: [null],
        city: [''],
        postalCode: [''],
        cadastralNumber: [''],
        buildingNumber: [''],
        buildingSectionNumber: [''],
        premiseType: [null],
        lastNumber: [1, Validators.required],
        environment: [this.premise.environment],
      },
      { validator: BusinessPremiseValidator },
    );

    this.editing = !!this.premise && this.premise.id;

    this.orgService.active
      .pipe(
        filter((org) => !!org),
        takeUntil(this.onDestroy$),
      )
      .subscribe((org) => {
        this.activeOrg = org;
      });

    if (this.editing) {
      this.businessPremiseForm.patchValue(this.premise);
      this.businessPremiseForm.controls.businessPremiseId.disable();

      const year = moment().get('year');
      const lastNumbers = this.premise.numbers;

      if (lastNumbers && lastNumbers[year]) {
        this.businessPremiseForm.controls.lastNumber.patchValue(
          lastNumbers[year] + 1,
        );

        // Prevent editing if number already incremented
        // This will cause false positives but it's low possibility of happening
        if (lastNumbers[year] !== 0) {
          this.businessPremiseForm.controls.lastNumber.disable();
        }
      }
    } else {
      this.businessPremiseForm.controls.businessPremiseId.patchValue(
        `P${this.existingPremises.length + 1}`,
      );
    }

    if (this.existingPremises.length === 0) {
      // const address = this.activeOrg.address.split(' ').slice(0, this.activeOrg.address.length - 2).join(' ');
      // const house = this.activeOrg.address.split(' ').slice(this.activeOrg.address.length - 2, this.activeOrg.address).join(' ');
      const addressA = this.activeOrg.address.split(' ');
      const street = addressA.slice(0, addressA.length - 1).join(' ');
      const houseNumber = addressA.slice(addressA.length - 1).join(' ');
      // const houseNumberAdditional =

      this.businessPremiseForm.patchValue({
        city: this.activeOrg.city,
        postalCode: this.activeOrg.zip,
        street,
        houseNumber,
      });
    }

    // Reset form
    this.businessPremiseForm
      .get('type')
      .valueChanges.pipe(takeUntil(this.onDestroy$))
      .subscribe(() => {
        this.businessPremiseForm.patchValue({
          street: '',
          houseNumber: '',
          houseNumberAdditional: null,
          community: null,
          city: '',
          postalCode: '',
          cadastralNumber: '',
          buildingNumber: '',
          buildingSectionNumber: '',
          premiseType: null,
          numbers: 1,
          environment: this.premise.environment,
        });

        this.sent = false;
      });
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  onClose(): void {
    this.activeModal.dismiss();
  }

  onSave(): void {
    this.sent = true;

    if (this.businessPremiseForm.valid) {
      const year = moment().get('year');
      const instance = this.businessPremiseForm.value;

      if (!instance.numbers) {
        instance.numbers = {};
      }

      instance.numbers[year] = instance.lastNumber - 1;

      if (instance.houseNumberAdditional === '') {
        instance.houseNumberAdditional = null;
      }

      if (instance.type === 'realestate') {
        instance.cadastralNumber = new Decimal(
          instance.cadastralNumber,
        ).toNumber();
        instance.buildingSectionNumber = new Decimal(
          instance.buildingSectionNumber,
        ).toNumber();
        instance.buildingNumber = new Decimal(
          instance.buildingNumber,
        ).toNumber();
      }

      if (this.editing) {
        delete instance.id;
      }

      this.loading = true;

      this.fursService[
        !this.editing ? 'createBusinessPremise' : 'updateBusinessPremise'
      ](
        this.activeOrg.id,
        this.editing ? this.premise.id : null,
        instance,
      ).subscribe(
        (businessPremise: BusinessPremise) => {
          this.loading = false;
          this.toastService.success('Successfully added Business premise');
          this.activeModal.close(businessPremise);
        },
        (err) => {
          if (err.status === 422) {
            this.toastService.warning(
              this.translate.instant(
                'furs.business-premise-add.validation-error.message',
              ),
            );
          } else {
            this.toastService.error(
              this.translate.instant('Error adding Business premise'),
            );
          }

          this.loading = false;
        },
      );
    }
  }
}
